// src/hooks/useMediaUpload.ts

import { useState, useCallback, useRef, useEffect } from 'react';

import { fetchWithAuth } from '../lib/fetchWithAuth';
import { getJwt, triggerRefresh } from '../lib/jwtHolder';
export interface MediaUploadStatus {
  fileId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  variants?: string[];
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    [key: string]: any;
  };
  error?: string;
}

export interface MediaUploadState {
  isUploading: boolean;
  uploadProgress: number;
  processingStatus: MediaUploadStatus | null;
  fileId: string | null;
  error: string | null;
}

const POLL_INTERVAL = 1000; // Poll every 1 second
const MAX_POLL_ATTEMPTS = 300; // Maximum 5 minutes (300 seconds)

export function useMediaUpload() {
  const [state, setState] = useState<MediaUploadState>({
    isUploading: false,
    uploadProgress: 0,
    processingStatus: null,
    fileId: null,
    error: null,
  });

  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Poll processing status until completed or failed
   */
  const pollStatus = useCallback(async (fileId: string): Promise<MediaUploadStatus> => {
    pollAttemptsRef.current = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          pollAttemptsRef.current++;

          if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
            reject(new Error('Processing timeout - maximum poll attempts exceeded'));
            return;
          }

          // fetchWithAuth attaches the Company JWT and handles refresh.
          const response = await fetchWithAuth(`/api/media/${fileId}/status`, {
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch status: ${response.statusText}`);
          }

          const status: MediaUploadStatus = await response.json();

          setState(prev => ({
            ...prev,
            processingStatus: status,
            uploadProgress: status.progress,
          }));

          if (status.status === 'completed') {
            resolve(status);
            return;
          }

          if (status.status === 'failed') {
            reject(new Error(status.error || 'Processing failed'));
            return;
          }

          // Continue polling
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }, []);

  /**
   * Upload a file and poll for processing completion
   */
  const uploadFile = useCallback(async (file: File): Promise<string> => {
    try {
      // Reset state
      setState({
        isUploading: true,
        uploadProgress: 0,
        processingStatus: null,
        fileId: null,
        error: null,
      });

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);

      // Create abort controller for polling fallback, and XHR for upload progress
      abortControllerRef.current = new AbortController();

      // Use XMLHttpRequest so we can track upload progress. The Company JWT
      // lives in the in-memory jwtHolder (not browser storage) — the same
      // source fetchWithAuth reads. On 401 we refresh once and replay.
      const sendUpload = (jwt: string | null): Promise<{ status: number; data: any }> =>
        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhrRef.current = xhr;
          xhr.open('POST', '/api/upload', true);
          xhr.withCredentials = true;
          if (jwt) {
            xhr.setRequestHeader('Authorization', `Bearer ${jwt}`);
          }

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              // Map upload progress 0-100 to 0-50 (processing will cover remaining)
              const percent = Math.round((event.loaded / event.total) * 50);
              setState(prev => ({ ...prev, uploadProgress: percent }));
            }
          };

          xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE) {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  resolve({ status: xhr.status, data: JSON.parse(xhr.responseText) });
                } catch (err) {
                  reject(new Error('Invalid JSON response from upload'));
                }
              } else if (xhr.status === 0) {
                reject(new Error('Upload aborted'));
              } else if (xhr.status === 401) {
                resolve({ status: 401, data: null });
              } else {
                try {
                  const parsed = JSON.parse(xhr.responseText || '{}');
                  reject(new Error(parsed.error || `Upload failed: ${xhr.statusText}`));
                } catch (err) {
                  reject(new Error(`Upload failed: ${xhr.statusText}`));
                }
              }
            }
          };

          xhr.onerror = () => reject(new Error('Network error during upload'));
          xhr.onabort = () => reject(new Error('Upload aborted'));

          xhr.send(formData);
        });

      let result = await sendUpload(getJwt());
      if (result.status === 401) {
        const fresh = await triggerRefresh();
        if (!fresh) {
          throw new Error('Session expired - please sign in again');
        }
        result = await sendUpload(fresh);
        if (result.status === 401) {
          throw new Error('Unauthorized');
        }
      }

      const data = result.data;
      const { fileId, jobId } = data;

      setState(prev => ({
        ...prev,
        fileId,
        uploadProgress: 10, // Upload complete, processing starting
      }));

      // Start polling for processing status. During processing we'll update progress from 50->100
      await pollStatus(fileId);

      // Processing complete
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadProgress: 100,
      }));

      return fileId;
    } catch (error: any) {
      const errorMessage = error.message || 'Upload failed';

      setState(prev => ({
        ...prev,
        isUploading: false,
        error: errorMessage,
      }));

      throw error;
    } finally {
      abortControllerRef.current = null;
      if (xhrRef.current) {
        try { xhrRef.current = null; } catch {}
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    }
  }, [pollStatus]);

  /**
   * Cancel ongoing upload/processing
   */
  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (xhrRef.current) {
      try { xhrRef.current.abort(); } catch {}
      xhrRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setState({
      isUploading: false,
      uploadProgress: 0,
      processingStatus: null,
      fileId: null,
      error: null,
    });
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    cancelUpload();
  }, [cancelUpload]);

  return {
    ...state,
    uploadFile,
    cancelUpload,
    reset,
  };
}

/**
 * Get media URL for displaying uploaded media
 */
export function getMediaUrl(fileId: string, variant?: 'thumbnail' | 'small' | 'medium' | 'large' | 'original'): string {
  if (variant === 'thumbnail') {
    return `/api/media/${fileId}/thumbnail`;
  }

  return `/api/media/${fileId}${variant ? `?variant=${variant}` : ''}`;
}
